import styled from '@emotion/styled';
import { AnchorHTMLAttributes, type ComponentProps, HTMLAttributes } from 'react';
import { Link as DomRouterLink, LinkProps as ReactRouterLinkProps } from 'react-router';
import { BORDER_WIDTH } from '../styles/borders';
import { COLOR } from '../styles/colors';
import { SPACING } from '../styles/spacing';
import linkStyles from './linkStyles';

export type LinkProps = ReactRouterLinkProps;

export const BaseLink = styled.a(linkStyles);

const Styled = {
  LinkButton: styled.button`
    ${linkStyles}
    /* button-specific overrides */
    appearance: none;
    border: ${BORDER_WIDTH.none};
    padding: 0;
    background: none;
    line-height: unset;
  `,

  FakeLink: BaseLink.withComponent('span'),

  RouterLink: BaseLink.withComponent(DomRouterLink),

  // A complete clickable area without any styles at all
  LinkArea: styled.a`
    text-decoration: none;
    cursor: pointer;
    outline: none;
    :focus-visible {
      outline: ${BORDER_WIDTH.sm} auto ${COLOR.blue};
      outline-offset: -${SPACING.sm};
    }
  `,
};

// Wrapper Link element that works both in bridged and unbridged environment
export default function Link(props: LinkProps) {
  return <Styled.RouterLink {...props} />;
}

// a Button that looks like a Link
export function LinkButton(props: ComponentProps<'button'>) {
  return <Styled.LinkButton type="button" {...props} />;
}

export function ExternalLink(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <BaseLink target="_blank" rel="noopener noreferrer" {...props} />;
}

export function MaybeExternalLink(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const { href } = props;
  let sameOrigin = false;

  // decide whether or not a link is internal or external
  if (href) {
    try {
      // When the link we are following does not match the current origin, treat it as external
      sameOrigin = window.location.origin === new URL(href).origin;
    } catch (e) {
      // we come here if the new URL(href) is invalid.
      // check if the link appears to be a route (i.e. looks like "/foo"). if so, treat it as internal
      if (href.charAt(0) === '/') {
        sameOrigin = true;
      }
    }
  }

  const LinkComponent = sameOrigin ? BaseLink : ExternalLink;
  return <LinkComponent {...props} />;
}

export function FakeLink(props: HTMLAttributes<HTMLSpanElement>) {
  return <Styled.FakeLink role="link" tabIndex={0} {...props} />;
}

const LinkAreaRouter = Styled.LinkArea.withComponent(DomRouterLink);

export function LinkArea(props: ReactRouterLinkProps) {
  return <LinkAreaRouter {...props} />;
}

export function ExternalLinkArea(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <Styled.LinkArea target="_blank" rel="noopener noreferrer" {...props} />;
}

export function RouterLink(props: LinkProps) {
  return <Styled.RouterLink {...props} />;
}
